export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export type LabelSide =
  | 'left'
  | 'right'
  | 'above'
  | 'below'
  | 'above-left'
  | 'above-right'
  | 'below-left'
  | 'below-right';

interface PlaceLabelParams {
  /** Screen-space rect of the annotation the label describes (y grows downward). */
  anchor: Rect;
  size: { width: number; height: number };
  /** The area the label must stay inside, typically the viewport canvas. */
  bounds: Rect;
  /** Areas the label must not cover if there is any way around it, e.g. other labels. */
  keepOut?: Rect[];
  /** Areas the label should avoid but may cover once nothing in `keepOut` is hit, e.g. tissue. */
  softKeepOut?: Rect[];
  /** Space between the anchor and the label. */
  gap?: number;
  /** The straight side to try first, and to keep when every side is equally good. */
  preferredSide?: StraightSide;
}

interface Placement {
  side: LabelSide;
  rect: Rect;
}

export type StraightSide = 'left' | 'right' | 'above' | 'below';

// Order doubles as the tie-break: left first matches how labels were always drawn.
const SIDES: StraightSide[] = ['left', 'right', 'above', 'below'];

/**
 * The straight side a direction points toward, from its screen-space change (y grows upward here,
 * as in display coordinates). Whichever axis it moves along the most wins.
 */
export function sideForDirection(dx: number, dy: number): StraightSide {
  if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? 'right' : 'left';
  return dy > 0 ? 'above' : 'below';
}

// Diagonal spots come after the straight sides, so a label only goes diagonal when that is better.
const CORNERS: LabelSide[] = ['above-left', 'above-right', 'below-left', 'below-right'];

// How far (in label sizes) a corner spot is pushed out along its diagonal past the anchor's corner,
// to get clear of an edge, such as the outline of the tissue, that runs at an angle.
const CORNER_STEPS = [0, 0.5, 1, 1.5, 2];

// Tissue is only known as rough rects, so a label that clips a small share of its own area on one
// still counts as off the tissue. Otherwise a spot with a sliver of overlap loses to a spot much
// further from the annotation that happens to be fully clear.
const SOFT_TOLERANCE = 0.15;

// Sliding a label away from where it would naturally sit (along its side, or out along a diagonal)
// costs this much per label size, in shares of the label's own area covering tissue. So a label
// only slides up or down to dodge tissue when that saves a good part of its area, and otherwise
// stays beside its annotation.
const SLIDE_COST = 0.5;

// A best spot that still covers more than this share of its area with tissue sends the search
// to the whole viewport.
const SEARCH_SOFT_SHARE = 0.5;

// Covered areas are floats, so two spots on the same amount of tissue can differ by a hair. They
// only count as different past this many square px, or a spot far away would win by rounding.
const AREA_EPSILON = 1;

// How far (in label sizes) a side may slide along its cross axis to dodge something in the way.
// 0 first, so an unobstructed label stays centered on the annotation.
const NUDGES = [0, 1, -1, 2, -2];

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function overlapArea(a: Rect, b: Rect): number {
  return (
    Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left)) *
    Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top))
  );
}

export function rectsOverlap(a: Rect, b: Rect): boolean {
  return overlapArea(a, b) > 0;
}

/** Total distance the rect sticks out past bounds, 0 when fully inside. */
function overflow(rect: Rect, bounds: Rect): number {
  return (
    Math.max(0, bounds.left - rect.left) +
    Math.max(0, rect.right - bounds.right) +
    Math.max(0, bounds.top - rect.top) +
    Math.max(0, rect.bottom - bounds.bottom)
  );
}

function moveTo(rect: Rect, left: number, top: number): Rect {
  return {
    left,
    top,
    right: left + (rect.right - rect.left),
    bottom: top + (rect.bottom - rect.top),
  };
}

function candidateFor(
  side: StraightSide,
  anchor: Rect,
  { width, height }: PlaceLabelParams['size'],
  gap: number,
  bounds: Rect,
  nudge: number
): Rect {
  const centerX = (anchor.left + anchor.right) / 2;
  const centerY = (anchor.top + anchor.bottom) / 2;
  const base = { left: 0, top: 0, right: width, bottom: height };

  // Only the cross axis is slid into bounds, so a side never jumps across the anchor.
  switch (side) {
    case 'left':
      return moveTo(
        base,
        anchor.left - gap - width,
        clamp(centerY - height / 2 + nudge * height, bounds.top, bounds.bottom - height)
      );
    case 'right':
      return moveTo(
        base,
        anchor.right + gap,
        clamp(centerY - height / 2 + nudge * height, bounds.top, bounds.bottom - height)
      );
    case 'above':
      return moveTo(
        base,
        clamp(centerX - width / 2 + nudge * width, bounds.left, bounds.right - width),
        anchor.top - gap - height
      );
    case 'below':
      return moveTo(
        base,
        clamp(centerX - width / 2 + nudge * width, bounds.left, bounds.right - width),
        anchor.bottom + gap
      );
  }
}

/** A spot diagonally off one of the anchor's corners, `step` label sizes further out. */
function cornerFor(
  side: LabelSide,
  anchor: Rect,
  { width, height }: PlaceLabelParams['size'],
  gap: number,
  bounds: Rect,
  step: number
): Rect {
  const isLeft = side.endsWith('left');
  const isAbove = side.startsWith('above');
  const left = isLeft ? anchor.left - gap - width * (1 + step) : anchor.right + gap + width * step;
  const top = isAbove
    ? anchor.top - gap - height * (1 + step)
    : anchor.bottom + gap + height * step;

  return moveTo(
    { left: 0, top: 0, right: width, bottom: height },
    clamp(left, bounds.left, bounds.right - width),
    clamp(top, bounds.top, bounds.bottom - height)
  );
}

/**
 * Picks the side of an annotation to draw its label on so the label stays inside `bounds`
 * and covers as little of `keepOut` as possible. Pure and in screen space, so it works for any
 * viewport size (e.g. tiles) and can be tested without VTK.
 *
 * A side that is blocked may slide along its cross axis before another side is considered.
 * If no side fits fully, the least-overflowing side is clamped into bounds.
 */
export function placeLabel({
  anchor,
  size,
  bounds,
  keepOut = [],
  softKeepOut = [],
  gap = 0,
  preferredSide,
}: PlaceLabelParams): Placement {
  const tolerance = SOFT_TOLERANCE * size.width * size.height;
  const labelArea = size.width * size.height;
  const score = (side: LabelSide, rect: Rect, slide = 0) => {
    const soft = coveredArea(rect, softKeepOut);
    const covered = soft <= tolerance ? 0 : soft;
    return {
      side,
      rect,
      overflow: overflow(rect, bounds),
      hard: coveredArea(rect, keepOut),
      soft: covered,
      // Without tissue to dodge there is nothing to trade, so sliding is free and side order rules.
      cost:
        softKeepOut.length === 0 || labelArea <= 0 ? 0 : covered / labelArea + SLIDE_COST * slide,
    };
  };

  const sides =
    preferredSide == null ? SIDES : [preferredSide, ...SIDES.filter((s) => s !== preferredSide)];
  const candidates = [
    ...sides.flatMap((side) =>
      NUDGES.map((nudge) =>
        score(side, candidateFor(side, anchor, size, gap, bounds, nudge), Math.abs(nudge))
      )
    ),
    ...CORNERS.flatMap((side) =>
      CORNER_STEPS.map((step) =>
        score(side, cornerFor(side, anchor, size, gap, bounds, step), 1 + step * 0.4)
      )
    ),
  ];

  // Staying inside the viewport matters most, then not covering things that must stay visible
  // (never traded for tissue), then staying off the tissue.
  let best = candidates.reduce((a, b) => {
    if (b.overflow !== a.overflow) return b.overflow < a.overflow ? b : a;
    if (b.hard !== a.hard) return b.hard < a.hard ? b : a;
    return b.cost < a.cost ? b : a;
  });

  // Nothing beside the annotation is clear (a dense cluster of labels, or an annotation deep in the
  // tissue), so look for the nearest free spot anywhere in the viewport instead of drawing on
  // top of something.
  if (best.overflow === 0 && (best.hard > 0 || best.soft > SEARCH_SOFT_SHARE * labelArea)) {
    const free = nearestFreeSpot({ best, anchor, size, bounds, score });
    if (free != null && (best.hard > 0 || free.soft < best.soft - AREA_EPSILON)) best = free;
  }

  return {
    side: best.side,
    rect: moveTo(
      best.rect,
      clamp(best.rect.left, bounds.left, bounds.right - size.width),
      clamp(best.rect.top, bounds.top, bounds.bottom - size.height)
    ),
  };
}

function coveredArea(rect: Rect, areas: Rect[]): number {
  return areas.reduce((sum, area) => sum + overlapArea(rect, area), 0);
}

// Roughly this many spots are tried along each axis when searching the whole viewport.
const SEARCH_STEPS = 24;

function nearestFreeSpot<T extends { side: LabelSide; rect: Rect; soft: number }>({
  best,
  anchor,
  size,
  bounds,
  score,
}: {
  best: T;
  anchor: Rect;
  size: PlaceLabelParams['size'];
  bounds: Rect;
  score: (side: LabelSide, rect: Rect) => T & { overflow: number; hard: number };
}): T | null {
  const spanX = bounds.right - bounds.left - size.width;
  const spanY = bounds.bottom - bounds.top - size.height;

  const centerX = (anchor.left + anchor.right) / 2;
  const centerY = (anchor.top + anchor.bottom) / 2;
  const stepX = Math.max(spanX / SEARCH_STEPS, 1);
  const stepY = Math.max(spanY / SEARCH_STEPS, 1);

  let found: { spot: T; soft: number; distance: number } | null = null;
  for (let x = 0; x <= spanX + stepX / 2; x += stepX) {
    for (let y = 0; y <= spanY + stepY / 2; y += stepY) {
      const rect = moveTo(
        { left: 0, top: 0, right: size.width, bottom: size.height },
        bounds.left + Math.min(x, spanX),
        bounds.top + Math.min(y, spanY)
      );
      const spot = score(best.side, rect);
      if (spot.overflow !== 0 || spot.hard !== 0) continue;

      const distance = Math.hypot(
        (rect.left + rect.right) / 2 - centerX,
        (rect.top + rect.bottom) / 2 - centerY
      );
      // Off the tissue first, then closest to the annotation.
      if (
        found == null ||
        spot.soft < found.soft - AREA_EPSILON ||
        (Math.abs(spot.soft - found.soft) <= AREA_EPSILON && distance < found.distance)
      ) {
        found = { spot, soft: spot.soft, distance };
      }
    }
  }

  return found?.spot ?? null;
}

export function isSameRect(a: Rect | null, b: Rect | null): boolean {
  return (
    a === b ||
    (a != null &&
      b != null &&
      a.left === b.left &&
      a.right === b.right &&
      a.top === b.top &&
      a.bottom === b.bottom)
  );
}

/** Shifts a rect so its origin moves by (-dx, -dy). */
export function offsetRect(rect: Rect, dx: number, dy: number): Rect {
  return {
    left: rect.left - dx,
    right: rect.right - dx,
    top: rect.top - dy,
    bottom: rect.bottom - dy,
  };
}

/**
 * Screen-space bounding rect (y up) of all eight corners of a world-space box. All corners are
 * projected because in sagittal or coronal views the box's axes don't line up with the screen's.
 */
export function projectBoundsToRect(
  bounds: readonly [number, number, number, number, number, number],
  project: (x: number, y: number, z: number) => readonly number[]
): Rect {
  const [xMin, xMax, yMin, yMax, zMin, zMax] = bounds;
  const points = [xMin, xMax].flatMap((x) =>
    [yMin, yMax].flatMap((y) => [zMin, zMax].map((z) => project(x, y, z)))
  );
  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);

  return {
    left: Math.min(...xs),
    right: Math.max(...xs),
    bottom: Math.min(...ys),
    top: Math.max(...ys),
  };
}

export interface PlacementArea {
  areaWidth: number;
  areaHeight: number;
  /** The annotation's rect in CSS px with y growing downward, matching placeLabel. */
  anchor: Rect;
  /** Areas the label should stay off if it can (e.g. tissue), in the same space as `anchor`. */
  softKeepOut: Rect[];
}

function toCssRect(rect: Rect, areaHeight: number, devicePixelRatio: number): Rect {
  return {
    left: rect.left / devicePixelRatio,
    right: rect.right / devicePixelRatio,
    top: areaHeight - rect.top / devicePixelRatio,
    bottom: areaHeight - rect.bottom / devicePixelRatio,
  };
}

/** Converts a display-space rect (device px, y up) and area size into placeLabel's space. */
export function toPlacementArea({
  anchorRect,
  keepOutRects = [],
  areaSize,
  devicePixelRatio,
}: {
  anchorRect: Rect;
  keepOutRects?: Rect[];
  areaSize: { width: number; height: number };
  devicePixelRatio: number;
}): PlacementArea {
  const areaHeight = areaSize.height / devicePixelRatio;
  return {
    areaWidth: areaSize.width / devicePixelRatio,
    areaHeight,
    anchor: toCssRect(anchorRect, areaHeight, devicePixelRatio),
    softKeepOut: keepOutRects.map((rect) => toCssRect(rect, areaHeight, devicePixelRatio)),
  };
}

// Least space, in CSS px, between an annotation and its label, so the label never touches the
// annotation or the handles drawn just outside it.
const LABEL_GAP = 8;

/**
 * Places a measured label inside a PlacementArea, staying off the area's tissue where it can and
 * never covering the given `keepOut` rects (other labels and annotations) if there's room. `outer` includes the label's border padding; `left`/`bottom` are the CSS offsets
 * (bottom measured from the area's bottom edge) of the content itself.
 */
export function placeLabelInArea({
  area,
  contentSize,
  borderOffset,
  keepOut,
  gap = LABEL_GAP,
  preferredSide,
}: {
  area: PlacementArea;
  contentSize: { width: number; height: number };
  borderOffset: number;
  keepOut: Rect[];
  /** Space in CSS px kept between the annotation and its label. */
  gap?: number;
  /** The side to try first, e.g. the end of a line the label belongs to. */
  preferredSide?: StraightSide;
}): { outer: Rect; left: number; bottom: number } {
  const { rect: outer } = placeLabel({
    anchor: area.anchor,
    size: {
      width: contentSize.width + borderOffset * 2,
      height: contentSize.height + borderOffset * 2,
    },
    bounds: { left: 0, top: 0, right: area.areaWidth, bottom: area.areaHeight },
    keepOut,
    softKeepOut: area.softKeepOut,
    gap,
    preferredSide,
  });

  return {
    outer,
    left: outer.left + borderOffset,
    bottom: area.areaHeight - (outer.bottom - borderOffset),
  };
}

// A viewport at least this big shows labels at their normal size; smaller ones shrink them.
const FULL_SIZE_AREA = { width: 360, height: 240 };
const MIN_LABEL_SCALE = 0.6;

/**
 * How much to shrink a label so it still fits a small viewport (e.g. a tile). Never above 1, so
 * labels are never drawn larger than their normal size, and never so small they can't be read.
 */
export function labelScaleForArea({
  areaWidth,
  areaHeight,
}: Pick<PlacementArea, 'areaWidth' | 'areaHeight'>): number {
  return clamp(
    Math.min(areaWidth / FULL_SIZE_AREA.width, areaHeight / FULL_SIZE_AREA.height),
    MIN_LABEL_SCALE,
    1
  );
}

/** Where a label sits relative to its annotation, so it can stay put as the view changes. */
export interface LabelOffset {
  dx: number;
  dy: number;
}

export function offsetFromAnchor(anchor: Rect, outer: Rect): LabelOffset {
  return { dx: outer.left - anchor.left, dy: outer.top - anchor.top };
}

/**
 * Puts a measured label back at a remembered offset from its annotation. It is not nudged back
 * inside the area, so panning moves the label with its annotation instead of sliding it along the
 * edge. Same result shape as `placeLabelInArea`.
 */
export function placeLabelAtOffset({
  area,
  contentSize,
  borderOffset,
  offset,
}: {
  area: PlacementArea;
  contentSize: { width: number; height: number };
  borderOffset: number;
  offset: LabelOffset;
}): { outer: Rect; left: number; bottom: number } {
  const width = contentSize.width + borderOffset * 2;
  const height = contentSize.height + borderOffset * 2;
  const outer = moveTo(
    { left: 0, top: 0, right: width, bottom: height },
    area.anchor.left + offset.dx,
    area.anchor.top + offset.dy
  );

  return {
    outer,
    left: outer.left + borderOffset,
    bottom: area.areaHeight - (outer.bottom - borderOffset),
  };
}

/**
 * Puts a measured label with its top left corner at (left, top), wherever that is, so it follows
 * its annotation when the view pans. Same result shape as `placeLabelInArea`.
 */
export function placeLabelAt({
  area,
  contentSize,
  borderOffset,
  left,
  top,
}: {
  area: PlacementArea;
  contentSize: { width: number; height: number };
  borderOffset: number;
  left: number;
  top: number;
}): { outer: Rect; left: number; bottom: number } {
  const width = contentSize.width + borderOffset * 2;
  const height = contentSize.height + borderOffset * 2;
  const outer = moveTo({ left: 0, top: 0, right: width, bottom: height }, left, top);

  return {
    outer,
    left: outer.left + borderOffset,
    bottom: area.areaHeight - (outer.bottom - borderOffset),
  };
}

export interface Point {
  x: number;
  y: number;
}

// Labels closer than this to their annotation are obviously its label, so they need no link line.
const MIN_LEADER_GAP = 16;

/**
 * The short line that ties a label to its annotation once they are apart: from the annotation's
 * edge to the label's edge, so it never runs across the annotation itself. Null when they are
 * close enough not to need one.
 */
export function leaderSegment(anchor: Rect, label: Rect): { from: Point; to: Point } | null {
  const gapX = Math.max(0, anchor.left - label.right, label.left - anchor.right);
  const gapY = Math.max(0, anchor.top - label.bottom, label.top - anchor.bottom);
  if (Math.hypot(gapX, gapY) < MIN_LEADER_GAP) return null;

  const labelCenter = { x: (label.left + label.right) / 2, y: (label.top + label.bottom) / 2 };
  const from = {
    x: clamp(labelCenter.x, anchor.left, anchor.right),
    y: clamp(labelCenter.y, anchor.top, anchor.bottom),
  };
  const to = {
    x: clamp(from.x, label.left, label.right),
    y: clamp(from.y, label.top, label.bottom),
  };

  return { from, to };
}
